// @ts-nocheck
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
// @ts-ignore
import { actions as sonataLIActions } from 'sonata-li';
import { GeoLookalikeReportsWizardWrapper } from 'sonata-platform-components';
import { ButtonUI, showNotification } from 'sonata-react-components';

import { getInitialTimeFrame, getMetricsKeys, STORE_VISITS } from '@/helpers/reporting.helpers';
import { setGeoLookALikeStatusChanged } from '@/store/actions/reporting.actions';
import { getBaseCurrencyName } from '@/store/selectors/baseCurrency.selectors';
import {
  getGeoLookALikeStatus,
  getMetricsAndKpisByType,
  getSelectedCampaigns
} from '@/store/selectors/reporting.selectors';
import { getCampaigns } from '@/store/selectors/reportingAdvancedFilter.selectors';
import { getUserRoles } from '@/store/selectors/resources.selectors';
import { getCurrentUser } from '@/store/selectors/user.selectors';
import { postGeoLookALike } from '@/ts-resources/reporting/geoLookALike.resources';

import type { GeoLookALike } from 'sonata-static-storage/lib/common/common.types';
import type { GeoLookALikeReportInterface } from 'sonata-static-storage/lib/schemas/geoLookALikeReport/geoLookALikeReport.schema';

type Info = Pick<
  GeoLookALikeReportInterface,
  'campaign' | 'createLayer' | 'name' | 'origin' | 'reportType' | 'variables'
> & {
  user: string;
  userFrom?: string;
};
type Report = GeoLookALike;

function GeoLookALikeButton() {
  const [showModal, setShowModal] = useState(false);
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const currentExport = useSelector(getGeoLookALikeStatus);
  const userRoles = useSelector(getUserRoles);
  const user = useSelector(getCurrentUser);
  const reportingCampaigns: {
    countries: string[];
    id: string;
    timezone: string;
  }[] = useSelector(getSelectedCampaigns);
  const selectedReportingCampaignsIds = useSelector(getCampaigns);
  const metricsAndKpi = useSelector(getMetricsAndKpisByType);
  const selectedCampaigns = reportingCampaigns.filter(({ id }) => selectedReportingCampaignsIds.includes(id));
  const initialDates = getInitialTimeFrame(
    reportingCampaigns.filter(({ id }) => selectedReportingCampaignsIds.includes(id))
  );
  const baseCurrencyName = useSelector(getBaseCurrencyName);

  const shouldButtonEnable =
    [
      ...new Set(
        selectedCampaigns.reduce(
          (accumulator: string[], current) => [...accumulator, ...(current?.countries || [])],
          []
        )
      )
    ].length === 1;
  const metricsAndKpiFiltered = metricsAndKpi
    .filter(item => !STORE_VISITS.includes(item))
    .map(item => getMetricsKeys(item));

  const handleModalOpen = () => setShowModal(true);
  const handleModalClose = () => setShowModal(false);
  const handleFinish = async ({ info, report }: { info: Info; report: Report }) => {
    const formatReport = {
      ...report,
      fieldGroups: report.fieldGroups?.length ? report.fieldGroups : undefined
    };
    try {
      const geoLookALikeData = await postGeoLookALike(info, formatReport);
      if (geoLookALikeData.reportId) {
        const reports = info.reportType.map(r => ({
          ...geoLookALikeData,
          reportType: [r]
        }));

        if (reports && reports.length) {
          showNotification(
            'success',
            formatMessage({
              defaultMessage: 'Export has been completed successfully! Process continues in the background.',
              id: 'reporting.geoLookALike.defaultSuccess'
            })
          );
          dispatch(sonataLIActions.geoLookALikeAddExport(geoLookALikeData?.reportId, reports));
          dispatch(
            setGeoLookALikeStatusChanged({
              fileUrl: geoLookALikeData.fileUrl,
              name: geoLookALikeData.name,
              reportId: geoLookALikeData.reportId,
              status: geoLookALikeData.status
            })
          );
        } else {
          showNotification(
            'success',
            formatMessage({
              defaultMessage:
                'Export has been completed successfully! Process continues in the background. The layer will be created automatically.',
              id: 'reporting.geoLookALike.sonataLiLayerSuccess'
            })
          );
        }
        handleModalClose();
      }
    } catch {
      showNotification(
        'error',
        formatMessage({
          defaultMessage: 'Geo LookALike Report creation failed',
          id: 'reporting.geoLookALike.error'
        })
      );
    }
  };

  const campaign = {
    beginDate: initialDates.beginDate,
    countries: selectedCampaigns[0].countries,
    currency: baseCurrencyName,
    endDate: initialDates.endDate,
    ids: selectedReportingCampaignsIds,
    kpiAndMetricsOptions: metricsAndKpiFiltered?.map(metric => ({
      id: metric,
      label: formatMessage({
        defaultMessage: metric,
        id: `reporting.metrics.${metric}`
      })
    })),
    timezone: selectedCampaigns[0].timezone,
    user: user.id || user._id
  };

  return (
    <>
      <ButtonUI
        className="reportingHeader__downloadButtonsGeo"
        disabled={!shouldButtonEnable}
        icon="fa-regular fa-file-pdf"
        id="reportingHeader_downloadButtons_geoLookALike"
        loading={currentExport.status === 'pending'}
        onClick={handleModalOpen}
        shape="link"
        size="small"
        text={formatMessage({
          defaultMessage: 'GeoLookALike report',
          id: 'reporting.header.buttons.geoLookALike'
        })}
        type="secondary"
      />
      {showModal ? (
        <GeoLookalikeReportsWizardWrapper
          campaign={campaign}
          isOpen={showModal}
          onClose={handleModalClose}
          onFinish={handleFinish}
          type="reporting"
          user={user}
          userRoles={userRoles}
        />
      ) : null}
    </>
  );
}

export default GeoLookALikeButton;
